import { useState, useEffect } from "react";
import {
  X, BookOpen, CheckCircle2, Lightbulb, Landmark,
  ThumbsUp, ThumbsDown, Search, Compass, ChevronRight,
  Sparkles
} from "lucide-react";
import { useHelp } from "../../lib/help";
import { HELP_TOPICS } from "../../constants/helpContent";

export default function HelpPanel() {
  const { isHelpOpen, activeTopic, topicData, closeHelp, openHelp, feedback, submitFeedback, startTour } = useHelp();
  const [searchTerm, setSearchTerm] = useState("");
  const [feedbackGiven, setFeedbackGiven] = useState(null);

  useEffect(() => {
    if (activeTopic && feedback[activeTopic]) {
      setFeedbackGiven(feedback[activeTopic]);
    } else {
      setFeedbackGiven(null);
    }
  }, [activeTopic, feedback]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isHelpOpen) {
        closeHelp();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isHelpOpen, closeHelp]);

  if (!isHelpOpen) return null;

  const currentTopic = topicData || HELP_TOPICS["noi"];
  const allTopicKeys = Object.keys(HELP_TOPICS);

  // Filtered topics for search
  const searchResults = searchTerm.trim()
    ? allTopicKeys.filter((key) => {
        const t = HELP_TOPICS[key];
        const q = searchTerm.toLowerCase();
        return (
          t.title.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q) ||
          t.short.toLowerCase().includes(q)
        );
      })
    : [];

  const handleFeedback = (isHelpful) => {
    submitFeedback(activeTopic, isHelpful);
    setFeedbackGiven(isHelpful ? "helpful" : "unhelpful");
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="help-panel">
      {/* Backdrop overlay */}
      <div
        onClick={closeHelp}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      />

      {/* Drawer surface: right drawer on desktop, bottom sheet on mobile */}
      <div className="relative w-full sm:w-[460px] h-[90vh] sm:h-full mt-auto sm:mt-0 bg-[var(--surface)] text-[var(--text)] sm:border-l border-[var(--border)] shadow-2xl flex flex-col z-10 rounded-t-3xl sm:rounded-none overflow-hidden animate-in slide-in-from-bottom sm:slide-in-from-right duration-300">
        {/* Header */}
        <div className="p-5 border-b border-[var(--border)] bg-[var(--surface)] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] text-white flex items-center justify-center shadow-md shrink-0">
              <BookOpen size={16} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase font-bold tracking-wider text-teal-400">
                  {currentTopic.category}
                </span>
              </div>
              <h2 className="text-base font-black text-[var(--text)] truncate">
                {currentTopic.title}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={closeHelp}
              data-testid="help-panel-close"
              className="p-2 rounded-xl text-[var(--muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)] transition-colors"
              aria-label="Close help"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Quick Topic Search */}
        <div className="p-3.5 bg-[var(--surface-hover)]/40 border-b border-[var(--border)]">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              type="text"
              placeholder="Search help topics (e.g. Schedule E, Mileage, Cap Rate)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-[var(--surface-hover)] border border-[var(--border)] text-xs text-[var(--text)] placeholder-[var(--muted)] focus:outline-none focus:border-teal-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-[var(--muted)] hover:text-[var(--text)]"
              >
                Clear
              </button>
            )}
          </div>

          {/* Search Dropdown Results */}
          {searchTerm.trim() && (
            <div className="mt-2 max-h-48 overflow-y-auto divide-y divide-[var(--border)] rounded-xl bg-[var(--surface)] border border-[var(--border)] shadow-lg">
              {searchResults.length === 0 ? (
                <div className="p-3 text-xs text-[var(--muted)] text-center">No topics match "{searchTerm}"</div>
              ) : (
                searchResults.map((key) => (
                  <button
                    key={key}
                    onClick={() => {
                      openHelp(key);
                      setSearchTerm("");
                    }}
                    className="w-full p-2.5 text-left text-xs hover:bg-[var(--surface-hover)] flex items-center justify-between group transition-colors"
                  >
                    <div>
                      <div className="font-semibold text-[var(--text)] group-hover:text-teal-500 dark:group-hover:text-teal-400">
                        {HELP_TOPICS[key].title}
                      </div>
                      <div className="text-[10px] text-[var(--muted)]">{HELP_TOPICS[key].category}</div>
                    </div>
                    <ChevronRight size={13} className="text-[var(--muted)] group-hover:translate-x-0.5 transition-transform" />
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6 text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
          {/* Quick Summary Pill */}
          <div className="p-3.5 rounded-2xl bg-teal-500/10 border border-teal-500/20 text-teal-800 dark:text-teal-200">
            <div className="text-[10px] uppercase tracking-wider font-bold text-teal-600 dark:text-teal-400 mb-1">
              At a Glance
            </div>
            <p className="text-xs text-teal-900 dark:text-teal-100 font-medium leading-relaxed">
              {currentTopic.short}
            </p>
          </div>

          {/* Detailed Plain-English Explanation */}
          <div>
            <h3 className="text-xs uppercase font-bold tracking-wider text-[var(--muted)] mb-2">
              How It Works & Why It Matters
            </h3>
            <p className="whitespace-pre-line text-xs sm:text-[13px] leading-relaxed text-[var(--text)]">
              {currentTopic.full}
            </p>
          </div>

          {/* Step-by-Step Guide */}
          {currentTopic.steps && currentTopic.steps.length > 0 && (
            <div>
              <h3 className="text-xs uppercase font-bold tracking-wider text-[var(--muted)] mb-3">
                Step-by-Step Instructions
              </h3>
              <div className="space-y-2.5">
                {currentTopic.steps.map((step, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-2.5 p-2.5 rounded-xl bg-[var(--surface-hover)] border border-[var(--border)]"
                  >
                    <span className="w-5 h-5 rounded-full bg-teal-500/20 text-teal-400 font-bold text-[10px] flex items-center justify-center shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <span className="text-xs text-[var(--text)] leading-relaxed">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actionable Landlord Tips */}
          {currentTopic.tips && currentTopic.tips.length > 0 && (
            <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20">
              <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-bold text-xs mb-2">
                <Lightbulb size={15} />
                <span>Landlord Pro-Tips</span>
              </div>
              <ul className="space-y-2">
                {currentTopic.tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-amber-900 dark:text-amber-100 text-xs leading-relaxed">
                    <span className="text-amber-500 dark:text-amber-400 shrink-0">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Official IRS Tax Reference */}
          {currentTopic.irs && (
            <div className="p-4 rounded-2xl bg-[var(--surface-hover)] border border-[var(--border)]">
              <div className="flex items-center gap-1.5 text-teal-600 dark:text-teal-400 font-bold text-xs mb-1.5">
                <Landmark size={15} />
                <span>IRS Tax Code & Form Reference</span>
              </div>
              <p className="text-[11.5px] text-[var(--text-secondary)] leading-relaxed">
                {currentTopic.irs}
              </p>
            </div>
          )}
        </div>

        {/* Footer: Interactive Feedback & App Tour Re-launch */}
        <div className="p-4 border-t border-[var(--border)] bg-[var(--surface)] flex items-center justify-between gap-3 shrink-0">
          <div>
            {feedbackGiven ? (
              <div className="text-xs text-teal-400 font-semibold flex items-center gap-1.5 animate-in fade-in">
                <CheckCircle2 size={14} />
                <span>Thanks for your feedback! {feedbackGiven === "helpful" ? "👍" : "👎"}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--muted)] font-medium">Was this helpful?</span>
                <button
                  type="button"
                  onClick={() => handleFeedback(true)}
                  className="p-1.5 rounded-lg border border-[var(--border)] bg-[var(--surface-hover)] hover:border-teal-500/40 hover:bg-teal-500/10 text-xs transition-colors flex items-center justify-center"
                  title="Yes, helpful"
                  data-testid="help-feedback-up"
                  aria-label="Helpful"
                >
                  <ThumbsUp size={14} className="text-slate-700 dark:text-slate-200 hover:text-teal-500 dark:hover:text-teal-400 transition-colors" />
                </button>
                <button
                  type="button"
                  onClick={() => handleFeedback(false)}
                  className="p-1.5 rounded-lg border border-[var(--border)] bg-[var(--surface-hover)] hover:border-red-500/40 hover:bg-red-500/10 text-xs transition-colors flex items-center justify-center"
                  title="No, needs improvement"
                  data-testid="help-feedback-down"
                  aria-label="Not helpful"
                >
                  <ThumbsDown size={14} className="text-slate-700 dark:text-slate-200 hover:text-red-500 dark:hover:text-red-400 transition-colors" />
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              closeHelp();
              startTour();
            }}
            className="text-xs text-teal-600 dark:text-teal-400 hover:text-teal-500 dark:hover:text-teal-300 font-semibold flex items-center gap-1.5 py-1.5 px-3 rounded-lg border border-teal-500/30 hover:bg-teal-500/10 transition-colors"
            data-testid="relaunch-tour-btn"
          >
            <Compass size={13} />
            <span>Take App Tour</span>
          </button>
        </div>
      </div>
    </div>
  );
}
